import {useState, useEffect, useRef, useCallback} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-custom-alert';
import { sendTimingLog } from '../utils/timingLogger'; 


/**
 * Maneja toda la lógica de negocio del relevamiento:
 * - Crear relevamiento
 * - Subir / eliminar fotos
 * - Guardar y aprobar
 *
 * @param {{ category: string|number, misProductos: boolean, tienda: string|number }} params
 */
export function useRelevamiento({ category, misProductos, tienda, isOnline }) {
    const navigate = useNavigate();

    // ── Estado ──────────────────────────────────────────────────────────────────
    const [idRelevamiento, setIdRelevamiento]         = useState(-1);
    const [imagesData, setImagesData]                 = useState([]);
    const [uploadingPending, setUploadingPending]     = useState(false);
    const [showBotonGuardar, setShowBotonGuardar]     = useState(false);
    const [dataSKU, setDataSKU]                       = useState({});
    const [dataOfertas, setDataOfertas]               = useState({});
    const [isModalOpen, setIsModalOpen]               = useState(false);
    const [isModalOfertasOpen, setIsModalOfertasOpen] = useState(false);
    const [isGuardando, setIsGuardando] = useState(false);

    // Espejo de `idRelevamiento` + promesa de creación en vuelo. Ver `ensureRelevamiento`.
    const idRelevamientoRef      = useRef(-1);
    const relevamientoPromiseRef = useRef(null);

    // ── Helpers ─────────────────────────────────────────────────────────────────
    const getAccessToken = () => {
        const userData = JSON.parse(localStorage.getItem('user'));
        return userData?.access ?? null;
    };

    /**
     * El backend devuelve `fotos_ids` con dos formas según el flujo: una lista `[12]`
     * en categoría completa y un escalar `12` en precio de cerca. Normalizamos acá
     * para no depender de esa inconsistencia.
     * @returns {number|null} id de la FotoRelevamiento, o null si no vino.
     */
    const normalizarFotoId = (fotosIds) => {
        const raw = Array.isArray(fotosIds) ? fotosIds[0] : fotosIds;
        const id = Number(raw);
        return Number.isInteger(id) && id > 0 ? id : null;
    };

    // ── Ocultar botón guardar si no quedan imágenes ──────────────────────────────
    useEffect(() => {
        // El botón se muestra si hay al menos una imagen que NO esté en error
        // y que ya se haya intentado subir (o que existan imágenes en la lista)
        const hayImagenesValidas = imagesData.some(img => img.imgStatus === 'done' || img.imgStatus === 'toUpload');

        if (imagesData.length === 0) {
            setShowBotonGuardar(false);
        } else {
            setShowBotonGuardar(hayImagenesValidas);
        }

    }, [imagesData]);



    // ── Crear relevamiento ───────────────────────────────────────────────────────
    const createRelevamiento = async (tiendaActual) => {
        const access = getAccessToken();
        const formData = new FormData();
        formData.append('categoria', category);
        formData.append('categoria_completa', misProductos);
        try {
            const response = await axios.post(
                process.env.REACT_APP_BACK_API_URL + 'api-interna/relevamiento/' + tiendaActual,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${access}`,
                    },
                }
            );
            if (response.status === 201) {
                const newId = response.data.id_relevamiento;
                setIdRelevamiento(newId);        // para el render
                idRelevamientoRef.current = newId; // para los closures que ya están corriendo
                return newId;
            }
        } catch (e) {
            console.error('Error al crear relevamiento:', e);
        }
    };

    /**
     * Devuelve el id del relevamiento, creándolo una sola vez.
     *
     * `idRelevamiento` vive en el state, así que un closure capturado mientras el POST de
     * creación está en vuelo lo sigue viendo en -1. Sin este candado, dos tandas cargadas
     * una atrás de la otra crean DOS relevamientos: las fotos quedan partidas entre ambos
     * y el que no se aprueba queda colgado en DB y storage.
     *
     * El candado es solo sobre la creación — las subidas siguen libres y concurrentes.
     */
    const ensureRelevamiento = (tiendaActual) => {
        if (idRelevamientoRef.current !== -1) {
            return Promise.resolve(idRelevamientoRef.current);
        }
        if (!relevamientoPromiseRef.current) {
            relevamientoPromiseRef.current = createRelevamiento(tiendaActual)
                .catch((e) => {
                    console.error('Error al crear relevamiento:', e);
                    return undefined;
                })
                .then((newId) => {
                    // Si falló, soltamos el candado para que el próximo intento reintente.
                    if (newId === undefined) relevamientoPromiseRef.current = null;
                    return newId;
                });
        }
        return relevamientoPromiseRef.current;
    };

    /** Vuelve al estado "sin relevamiento". Los refs tienen que acompañar al state. */
    const resetRelevamiento = () => {
        setIdRelevamiento(-1);
        idRelevamientoRef.current = -1;
        relevamientoPromiseRef.current = null;
    };

    // ── Inicializar imágenes localmente ──────────────────────────────────────────
    const initImages = (e) => {
        setShowBotonGuardar(true);
        const files     = Array.from(e.target.files);
        const newImages = files.map((file) => {
            const url = URL.createObjectURL(file);
            return {
                id:         url,
                file,
                previewUrl: url,
                imgStatus:  'toUpload',
                boxes:      [],
                idBackend:  -1,
            };
        });
        setImagesData((prev) => [...prev, ...newImages]);
    };

    // __ Funcion para que se descargue .json por imagen automáticamente
    function downloadJson(data, filename) {
        const blob = new Blob(
            [JSON.stringify(data, null, 2)],
            { type: "application/json" }
        );
    
        const url = window.URL.createObjectURL(blob);
    
        const a = document.createElement("a");
        a.href = url;
        a.download = filename.replace(/\.[^/.]+$/, "") + ".json";
    
        document.body.appendChild(a);
        a.click();
        a.remove();
    
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 100);
    }

    /**
     * Sube al backend las imágenes que recibe — solo esas.
     *
     * Ojo: NO filtrar acá por `imgStatus === 'toUpload'` sobre la lista completa. Ese
     * estado significa "todavía no me contestaron", no "todavía no la mandé", así que
     * una foto en pleno POST lo tiene puesto y una segunda tanda la volvería a subir.
     * Cada re-envío crea otra FotoRelevamiento en la DB, pero la tarjeta de la UI guarda
     * un solo `idBackend`: la fila anterior queda huérfana, invisible para el borrado y
     * sumando cantidades al aprobar.
     *
     * El `Promise.all` tampoco es cosmético: con `forEach(async …)` las promesas se
     * descartan y `uploadAll` resuelve antes de comprimir la primera imagen, o sea que
     * quien la llama no puede saber cuándo terminó la subida.
     */
    const uploadAll = async (idRelevamientoToUpload, imagesToUpload) => {
        const access = getAccessToken();

        await Promise.all(imagesToUpload.map(async (img) => {
            // ─── TIMING ───
            const t0 = performance.now();
            const originalKB = Math.round(img.file.size / 1024);
            const sizeMB = (img.file.size / 1024 / 1024).toFixed(2);
            console.log(`[TIMING] ▶ Inicio upload: ${img.file.name} (${sizeMB} MB)`);
            // ──────────────

            const formdata_image = new FormData();
            const comprimirImagen = (file, maxSize = 1280, quality = 0.9) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    const url = URL.createObjectURL(file);
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        let { width, height } = img;
                        if (Math.max(width, height) > maxSize) {
                            const scale = maxSize / Math.max(width, height);
                            width  = Math.round(width * scale);
                            height = Math.round(height * scale);
                        }
                        canvas.width  = width;
                        canvas.height = height;
                        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                        canvas.toBlob(
                            (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
                            'image/jpeg',
                            quality
                        );
                        URL.revokeObjectURL(url);
                    };
                    img.src = url;
                });
            };

            const compressedFile = await comprimirImagen(img.file); // ~3.5MB → ~300KB
            const tCompresion    = performance.now();
            const compressedKB   = Math.round(compressedFile.size / 1024); 

            let tUpload = null;   // declarar acá para poder setear dentro del callback
            
            formdata_image.append('file', compressedFile);          // en vez de img.file
            // formdata_image.append('file', img.file);
            formdata_image.append('categoria', category);
            formdata_image.append('categoria_completa', misProductos);

            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_BACK_API_URL}api-interna/ampliar_relevamiento/${idRelevamientoToUpload}`,
                    formdata_image,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${access}`,
                        },
                        onUploadProgress: (progressEvent) => {
                            tUpload = performance.now(); // siempre actualizar → el último valor = fin del upload
                            const { loaded, total } = progressEvent;
                            if (total > 0) {
                                const percent = Math.round((loaded * 100) / total);
                                setImagesData((prev) =>
                                    prev.map((i) => i.id === img.id ? { ...i, progress: percent } : i)
                                );
                            }
                        },
                    }
                );
                
                // ─── TIMING: tiempo total incluyendo inferencia ML ───
                const data   = res.data;
                const tTotal = performance.now();
                const bt     = data._timing || {};

                sendTimingLog({  // fire-and-forget
                    filename:            img.file.name,
                    original_kb:         originalKB,
                    compressed_kb:       compressedKB,
                    compression_params:  { maxSize: 1280, quality: 0.9 },
                    t_compression_s:     parseFloat(((tCompresion - t0)        / 1000).toFixed(3)),
                    t_upload_s:          tUpload !== null ? parseFloat(((tUpload - tCompresion) / 1000).toFixed(3)) : null,
                    t_total_client_s:    parseFloat(((tTotal     - t0)          / 1000).toFixed(3)),
                    ...bt,   // django_pre_ml_s, ml_save_files_s, ml_inference_s, django_post_ml_s, etc.
                  });

                
                const boxesImage = data.info_productos_por_img[img.file.name] || [];
                const fotoIdBackend = normalizarFotoId(data.fotos_ids);
                const sinProductos = !misProductos && boxesImage.length === 0;

                // Ojo: `sinProductos` NO es un error de subida. La foto se guardó bien en el
                // backend, así que hay que quedarse con el idBackend igual — sin él, borrarla
                // desde la app la sacaría de la UI pero dejaría la FotoRelevamiento huérfana.
                setImagesData((prev) =>
                    prev.map((i) => {
                        if (i.id !== img.id) return i;
                        return sinProductos
                            ? { ...i, imgStatus: 'sinDatos', boxes: [], idBackend: fotoIdBackend, progress: 100 }
                            : { ...i, imgStatus: 'done', boxes: boxesImage, idBackend: fotoIdBackend, progress: 100 };
                    })
                );
            } catch (err) {
                if (!err.response) {
                    // Sin marcarla, la foto queda en 'toUpload' para siempre: nadie va a
                    // reintentarla (este callback ya corrió) y el botón Guardar se bloquea
                    // esperando una inferencia que no llega.
                    setImagesData((prev) => prev.map((i) => i.id === img.id ? { ...i, imgStatus: 'error' } : i));
                    toast.error("Se perdió la conexión durante el guardado. Intentá de nuevo.");
                    return; // importante: si no hay respuesta, no sigas evaluando err.response.status
                }

                if (err.response.status === 422) {
                    toast.error(err.response.data.message);
                    setImagesData([]);
                    resetRelevamiento();
                    return;
                } else {
                    setImagesData((prev) => prev.map((i) => i.id === img.id ? { ...i, imgStatus: 'error' } : i));
                }
            }
        }));
    };

    // ── Handler: cargar fotos desde el input ─────────────────────────────────────
    const handleCargarFotos = async (e, tiendaActual, benchmarkMode = false) => {
        if(!isOnline) {
            toast.error("Error de conexión.")
            return;
        }
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        e.target.value = "";
        try{
            // A propósito no hay candado global: se pueden encolar tandas mientras otras
            // suben. `uploadAll` manda solo las imágenes de esta tanda, así que ninguna
            // se sube dos veces, y `ensureRelevamiento` garantiza un único relevamiento.
            const currentId = await ensureRelevamiento(tiendaActual);
            if (currentId === undefined || currentId === -1) {
                toast.error('No se pudo iniciar el relevamiento. Intentá de nuevo.');
                return;
            }

            // ─── BENCHMARK EN PARALELO (dev only, no bloquea el flujo) ───
            if (benchmarkMode) {
                const runAll = async () => {
                    const qualities = [1.0, 0.9, 0.8, 0.7, 0.6, 0.5];
                    const resoluciones = [
                        { label: 'original (sin resize)', maxSize: 99999 },
                        { label: '1280px',                maxSize: 1280  },
                        { label: '1024px',                maxSize: 1024  },
                        { label: '960px',                 maxSize: 960   },
                    ];
                    for (const { label, maxSize } of resoluciones) {
                        console.log(`\n\n🔬 ===== RESOLUCIÓN: ${label} =====`);
                        await benchmarkBackendQuality(files, currentId, { qualities, maxSize });
                    }
                };
                runAll().catch(err => console.error('Benchmark falló:', err));
            }

            const newImages = files.map((file) => ({
                id: URL.createObjectURL(file),
                file,
                previewUrl: URL.createObjectURL(file),
                imgStatus: 'toUpload',
                progress: 0,
                boxes: [],
                idBackend: -1,
            }));

            // Update funcional y ANTES de subir. Con `setImagesData([...imagesData, …])`
            // se pisaba el estado con el `imagesData` congelado del render del click, así
            // que una foto que terminaba mientras tanto perdía su `imgStatus: 'done'` y su
            // `idBackend`, y volvía a quedar como pendiente.
            setImagesData((prev) => [...prev, ...newImages]);

            await uploadAll(currentId, newImages);
        } finally {
            e.target.value = "";
        }


    };

    // ── Handler: eliminar foto ────────────────────────────────────────────────────
    const handleEliminarFoto = async (id, idBackend) => {
        // Sin id de backend no hay nada que borrar allá (subida fallida): se saca y listo.
        if (!idBackend || idBackend === -1) {
            setImagesData((prev) => prev.filter((img) => img.id !== id));
            return;
        }

        // Con id sí hay fila en la DB: recién la sacamos de la lista cuando el backend
        // confirma. Si borráramos primero y el POST fallara, quedaría la foto huérfana
        // sin que el usuario se entere.
        const statusPrevio = imagesData.find((img) => img.id === id)?.imgStatus ?? 'done';

        setImagesData((prev) =>
            prev.map((img) => img.id === id ? { ...img, imgStatus: 'borrando' } : img)
        );

        try {
            const access = getAccessToken();
            await axios.post(
                process.env.REACT_APP_BACK_API_URL +
                'api-interna/quitar_foto_relevamiento/' +
                idRelevamiento + '/' + idBackend,
                null,
                { headers: { 'Authorization': `Bearer ${access}` } }
            );
            setImagesData((prev) => prev.filter((img) => img.id !== id));
        } catch (e) {
            console.error('Error al eliminar foto:', e);
            setImagesData((prev) =>
                prev.map((img) => img.id === id ? { ...img, imgStatus: statusPrevio } : img)
            );
            toast.error('No se pudo eliminar la foto. Intentá de nuevo.');
        }
    };

    // ── Handler: guardar (abre modal de preview) ─────────────────────────────────
    const handleGuardar = async () => {
        const hayImagenesProcesando = imagesData.some(
            (img) => img.imgStatus === 'toUpload'
        );

        if (hayImagenesProcesando) {
            toast.info('Esperá a que se terminen de procesar todas las fotos.', {
                toastStyle: { boxShadow: 'none!important' },
                floatingTime: 3500
            });
            return;
        }

        if (idRelevamiento === -1) {
            toast.info('¡Subí fotos para generar un nuevo relevamiento!', {
                toastStyle: { boxShadow: 'none!important' },
                floatingTime: 3500,
            });
            return;
        }

        try {
            const access = getAccessToken();
            const response = await axios.get(
                process.env.REACT_APP_BACK_API_URL +
                'api-interna/get_resultados_relevamiento_pendiente/' + idRelevamiento,
                { headers: { 'Authorization': `Bearer ${access}` } }
            );

            // Si es 200/201, seguimos normal
            setDataSKU(response.data);
            setIsModalOpen(true);

        } catch (e) {
            // --- AQUÍ CAPTURAMOS EL 422 ---
            if (e.response && e.response.status === 422) {
                // Mostramos el mensaje exacto que viene de Django
                toast.error(e.response.data.message || 'Error de validación de productos', {
                    floatingTime: 5000,
                });
                console.warn('Validación de negocio:', e.response.data);
            } else {
                // Errores genéricos (500, Red, etc.)
                console.error('Error al obtener resultados:', e);
                toast.error('Ocurrió un error inesperado al procesar los datos.');
            }
        }
    };

    // ── Handler: aprobar relevamiento ────────────────────────────────────────────
    const handleSubmit = () => {
        const todasConResultado = imagesData.every(
            (img) => img.imgStatus === 'done'
        );
        if (!todasConResultado) {
            setIsModalOpen(false);
            toast.error(
                'No es posible guardar un relevamiento con fotos que no contengan datos válidos.',
                { floatingTime: 2500 }
            );
            return;
        }

        setIsModalOpen(false);
        const access = getAccessToken();

        axios
            .post(
                process.env.REACT_APP_BACK_API_URL +
                'api-interna/aprobar_relevamiento/' + idRelevamiento,
                {},
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${access}`,
                    },
                }
            )
            .then((response) => {
                const ofertas = response.data['ofertas_de_venta'];
                if (ofertas.length > 0) {
                    setDataOfertas(ofertas);
                    setIsModalOfertasOpen(true);
                } else {
                    relevamientoAprobado();
                }
            })
            .catch((e) => {
                console.error(e);
                toast.error('Ocurrió un error durante la confirmación.', {
                    floatingTime: 2500,
                });
            });
    };

    // ── Relevamiento aprobado: resetear estado ───────────────────────────────────
    const restartPagina = () => {
        setImagesData([]);
        setDataSKU({});
        setShowBotonGuardar(false);
        resetRelevamiento();
        setTimeout(() => navigate('/', { replace: true }), 1500);
    };

    const relevamientoAprobado = () => {
        setIsModalOfertasOpen(false);
        toast.success('La auditoría fue confirmada', { floatingTime: 1500 });
        restartPagina();
    };

    const benchmarkBackendQuality = useCallback(async (
        files,
        idRelevamientoToUpload,
        {
            qualities = [1.0, 0.90, 0.82, 0.75, 0.65, 0.55, 0.45, 0.35, 0.25],
            maxSize = 1024,
            iouThreshold = 0.7,
        } = {}
    ) => {
        const access = getAccessToken();

        const comprimirImagen = (file, maxSize, quality) => {
            return new Promise((resolve) => {
                const img = new Image();
                const url = URL.createObjectURL(file);
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    if (Math.max(width, height) > maxSize) {
                        const scale = maxSize / Math.max(width, height);
                        width  = Math.round(width * scale);
                        height = Math.round(height * scale);
                    }
                    canvas.width  = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    canvas.toBlob(
                        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
                        'image/jpeg',
                        quality
                    );
                    URL.revokeObjectURL(url);
                };
                img.src = url;
            });
        };

        const getBoxCoords = (box) => {
            if (Array.isArray(box)) return { x1: box[0], y1: box[1], x2: box[2], y2: box[3] };
            if (box.x1 !== undefined) return { x1: box.x1, y1: box.y1, x2: box.x2, y2: box.y2 };
            if (box.bbox) return { x1: box.bbox[0], y1: box.bbox[1], x2: box.bbox[0] + box.bbox[2], y2: box.bbox[1] + box.bbox[3] };
            return null;
        };

        const computeIoU = (boxA, boxB) => {
            const a = getBoxCoords(boxA);
            const b = getBoxCoords(boxB);
            if (!a || !b) return 0;
            const xLeft   = Math.max(a.x1, b.x1);
            const yTop    = Math.max(a.y1, b.y1);
            const xRight  = Math.min(a.x2, b.x2);
            const yBottom = Math.min(a.y2, b.y2);
            if (xRight < xLeft || yBottom < yTop) return 0;
            const intersection = (xRight - xLeft) * (yBottom - yTop);
            const areaA = (a.x2 - a.x1) * (a.y2 - a.y1);
            const areaB = (b.x2 - b.x1) * (b.y2 - b.y1);
            const union = areaA + areaB - intersection;
            return union > 0 ? intersection / union : 0;
        };

        const getConfidence = (box) => {
            return box.confidence ?? box.score ?? box.conf ?? box.probability ?? 0;
        };

        const compareBoxes = (baselineBoxes, testBoxes) => {
            const matched = new Set();
            let truePositives = 0;
            const confDiffs = [];

            baselineBoxes.forEach((bBox) => {
                let bestIoU = 0;
                let bestTestIdx = -1;
                testBoxes.forEach((tBox, tIdx) => {
                    if (matched.has(tIdx)) return;
                    const iou = computeIoU(bBox, tBox);
                    if (iou > bestIoU) {
                        bestIoU = iou;
                        bestTestIdx = tIdx;
                    }
                });
                if (bestIoU >= iouThreshold) {
                    truePositives++;
                    matched.add(bestTestIdx);
                    confDiffs.push(getConfidence(testBoxes[bestTestIdx]) - getConfidence(bBox));
                }
            });

            const falseNegatives = baselineBoxes.length - truePositives;
            const falsePositives = testBoxes.length - truePositives;
            const recall = baselineBoxes.length > 0 ? truePositives / baselineBoxes.length : 1;
            const precision = testBoxes.length > 0 ? truePositives / testBoxes.length : 1;
            const f1 = (precision + recall) > 0 ? (2 * precision * recall) / (precision + recall) : 0;
            const avgConfDiff = confDiffs.length > 0
                ? confDiffs.reduce((s, d) => s + d, 0) / confDiffs.length
                : 0;

            return { truePositives, falseNegatives, falsePositives, recall, precision, f1, avgConfDiff };
        };

        const uploadAndGetBoxes = async (file) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('categoria', category);              // ← del closure del hook
            fd.append('categoria_completa', misProductos); // ← del closure del hook

            const t0 = performance.now();
            const res = await axios.post(
                `${process.env.REACT_APP_BACK_API_URL}api-interna/ampliar_relevamiento/${idRelevamientoToUpload}`,
                fd,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${access}`,
                    },
                }
            );
            const t1 = performance.now();
            const boxes = res.data.info_productos_por_img[file.name].bboxes || [];
            return { boxes, totalMs: t1 - t0, sizeKB: file.size / 1024 };
        };

        // ─── EJECUCIÓN ───
        console.log('\n╔════════════════════════════════════════════════════════════╗');
        console.log('║          BENCHMARK DE COMPRESIÓN vs DETECCIÓN ML           ║');
        console.log('╚════════════════════════════════════════════════════════════╝');
        console.log(`Imágenes: ${files.length} | Calidades: ${qualities.join(', ')} | maxSize: ${maxSize}px\n`);

        const globalStats = {};
        qualities.forEach(q => {
            globalStats[q] = {
                totalSize: 0, totalTime: 0,
                recalls: [], precisions: [], f1s: [], confDiffs: [],
                missedBoxes: 0, extraBoxes: 0, baselineBoxCount: 0,
            };
        });

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            console.log(`\n📸 [${i + 1}/${files.length}] ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
            console.log('─'.repeat(76));

            const baselineFile = await comprimirImagen(file, maxSize, 1.0);
            let baselineResult;
            try {
                baselineResult = await uploadAndGetBoxes(baselineFile);
            } catch (err) {
                console.error(`  ❌ Falló baseline, saltando imagen: ${err.message}`);
                continue;
            }

            const baselineBoxes = baselineResult.boxes;
            const baselineAvgConf = baselineBoxes.length > 0
                ? baselineBoxes.reduce((s, b) => s + getConfidence(b), 0) / baselineBoxes.length
                : 0;

            console.log(
                `  📌 BASELINE (q=1.00): ${baselineBoxes.length} det │ ` +
                `conf prom: ${baselineAvgConf.toFixed(3)} │ ` +
                `${baselineResult.sizeKB.toFixed(0)} KB │ ${baselineResult.totalMs.toFixed(0)} ms`
            );

            if (baselineBoxes.length === 0) {
                console.warn(`  ⚠️  Sin detecciones en baseline, saltando.`);
                continue;
            }

            for (const q of qualities) {
                if (q === 1.0) {
                    globalStats[q].totalSize += baselineResult.sizeKB;
                    globalStats[q].totalTime += baselineResult.totalMs;
                    globalStats[q].recalls.push(1);
                    globalStats[q].precisions.push(1);
                    globalStats[q].f1s.push(1);
                    globalStats[q].confDiffs.push(0);
                    globalStats[q].baselineBoxCount += baselineBoxes.length;
                    continue;
                }

                try {
                    const compressed = await comprimirImagen(file, maxSize, q);
                    const result = await uploadAndGetBoxes(compressed);
                    const cmp = compareBoxes(baselineBoxes, result.boxes);

                    globalStats[q].totalSize += result.sizeKB;
                    globalStats[q].totalTime += result.totalMs;
                    globalStats[q].recalls.push(cmp.recall);
                    globalStats[q].precisions.push(cmp.precision);
                    globalStats[q].f1s.push(cmp.f1);
                    globalStats[q].confDiffs.push(cmp.avgConfDiff);
                    globalStats[q].missedBoxes += cmp.falseNegatives;
                    globalStats[q].extraBoxes += cmp.falsePositives;
                    globalStats[q].baselineBoxCount += baselineBoxes.length;

                    const recallPct = (cmp.recall * 100).toFixed(1);
                    const f1Pct = (cmp.f1 * 100).toFixed(1);
                    const confSign = cmp.avgConfDiff >= 0 ? '+' : '';

                    console.log(
                        `  q=${q.toFixed(2)}: ${result.boxes.length.toString().padStart(3)} det │ ` +
                        `recall ${recallPct.padStart(5)}% │ F1 ${f1Pct.padStart(5)}% │ ` +
                        `Δconf ${confSign}${cmp.avgConfDiff.toFixed(3)} │ ` +
                        `${result.sizeKB.toFixed(0).padStart(4)} KB │ ${result.totalMs.toFixed(0).padStart(4)} ms`
                    );
                } catch (err) {
                    console.error(`  ❌ q=${q.toFixed(2)} falló: ${err.message}`);
                }
            }
        }

        // ─── RESUMEN ───
        console.log('\n\n╔════════════════════════════════════════════════════════════╗');
        console.log('║                    RESUMEN GLOBAL                          ║');
        console.log('╚════════════════════════════════════════════════════════════╝\n');
        console.log('Calidad │ Recall │   F1   │  Δconf │ Tam. avg │ Tiempo avg │ Perdidas │ Extras');
        console.log('─'.repeat(85));

        [...qualities].sort((a, b) => b - a).forEach(q => {
            const s = globalStats[q];
            const n = s.recalls.length;
            if (n === 0) return;
            const avgRecall    = (s.recalls.reduce((a, b) => a + b, 0) / n * 100).toFixed(1);
            const avgF1        = (s.f1s.reduce((a, b) => a + b, 0) / n * 100).toFixed(1);
            const avgConfDiff  = (s.confDiffs.reduce((a, b) => a + b, 0) / n).toFixed(3);
            const avgSize      = (s.totalSize / n).toFixed(0);
            const avgTime      = (s.totalTime / n).toFixed(0);
            const confSign = avgConfDiff >= 0 ? '+' : '';

            console.log(
                `  ${q.toFixed(2)}  │ ${avgRecall.padStart(5)}% │ ${avgF1.padStart(5)}% │ ` +
                `${confSign}${avgConfDiff.padStart(5)} │ ${avgSize.padStart(5)} KB │ ` +
                `${avgTime.padStart(6)} ms │ ${s.missedBoxes.toString().padStart(7)}  │ ${s.extraBoxes.toString().padStart(5)}`
            );
        });
        return globalStats;
    }, [category, misProductos]);  // ← dependencies del useCallback

    // ── API pública del hook ─────────────────────────────────────────────────────
    return {
        // Estado
        idRelevamiento,
        imagesData,
        showBotonGuardar,
        dataSKU,
        dataOfertas,
        isModalOpen,       setIsModalOpen,
        isModalOfertasOpen,
        benchmarkBackendQuality,
        // Handlers
        handleCargarFotos,
        handleEliminarFoto,
        handleGuardar,
        handleSubmit,
        relevamientoAprobado,
    };
}
